import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, STATUS_LABELS, STATUS_COLORS, PRIORITY_COLORS, workflowFor } from '../UserContext';
import { useWorkItemSync } from '../hooks/useWorkItemSync';

const API = `${window.location.protocol}//${window.location.hostname}/api`;
function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(r => r.json());
}

const PRIORITY_ORDER = { critical: 0, high: 1, normal: 2, low: 3 };

function PriorityBadge({ priority }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.06em',
      padding: '2px 6px', borderRadius: 4,
      backgroundColor: PRIORITY_COLORS[priority] + '22',
      color: PRIORITY_COLORS[priority],
      border: `1px solid ${PRIORITY_COLORS[priority]}44`,
    }}>{priority}</span>
  );
}

function WorkCard({ item, onClick }) {
  const mopPct = item.mop_total > 0 ? Math.round((item.mop_done / item.mop_total) * 100) : null;
  const statusColor = STATUS_COLORS[item.status] || '#64748b';
  return (
    <div
      onClick={() => onClick(item.id)}
      style={{
        backgroundColor: '#1e293b', borderRadius: 8, padding: '12px 14px',
        border: `1px solid ${statusColor}44`,
        borderLeft: `3px solid ${statusColor}`,
        cursor: 'pointer', marginBottom: 8, transition: 'all 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#243050'}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1e293b'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 13, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.title}
          </div>
          {item.sn_ticket && <div style={{ color: '#3b82f6', fontSize: 11 }}>SN: {item.sn_ticket}</div>}
        </div>
        <PriorityBadge priority={item.priority} />
      </div>

      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
        {item.location_name && <span>📍 {item.location_name}</span>}
        {item.tech_name && <span>👷 {item.tech_name}</span>}
        {item.fe_name && <span>🔧 {item.fe_name}</span>}
        {item.scheduled_date && <span>📅 {new Date(item.scheduled_date).toLocaleDateString()}</span>}
      </div>

      {mopPct !== null && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#64748b', marginBottom: 3 }}>
            <span>MOP Progress</span><span>{item.mop_done}/{item.mop_total}</span>
          </div>
          <div style={{ height: 4, backgroundColor: '#0f172a', borderRadius: 2 }}>
            <div style={{ height: 4, backgroundColor: mopPct === 100 ? '#10b981' : '#3b82f6', borderRadius: 2, width: `${mopPct}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {item.fmp_count > 0 && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#f97316' }}>📄 {item.fmp_count} FMP{item.fmp_count > 1 ? 's' : ''}</div>
      )}
    </div>
  );
}

const MANAGER_ROLES = new Set(['admin', 'rpm', 'management']);

function ReassignmentQueue({ users, onNavigate }) {
  const [queue, setQueue] = useState([]);
  const [qLoading, setQLoading] = useState(true);
  const [reassigning, setReassigning] = useState(null);
  const [newTechId, setNewTechId] = useState('');
  const [qError, setQError] = useState('');
  const [qStatus, setQStatus] = useState('');

  const load = useCallback(() => {
    setQLoading(true);
    api('/work-items/reassignment-queue')
      .then(rows => { setQueue(Array.isArray(rows) ? rows : []); setQLoading(false); })
      .catch(() => setQLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitReassign = async () => {
    if (!newTechId) { setQError('Select a technician'); return; }
    setQError('');
    try {
      await api(`/work-items/${reassigning.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ assigned_tech: Number(newTechId) }),
      });
      setQStatus(`Reassigned to ${users.find(u => u.id === Number(newTechId))?.name || 'tech'}`);
      setReassigning(null);
      setNewTechId('');
      load();
    } catch (err) {
      setQError(err.message);
    }
  };

  const techs = users.filter(u => u.role === 'tech' || u.role === 'field_engineer');
  const inputStyle = { padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  if (qLoading) return <div style={{ color: '#64748b', padding: 20 }}>Loading reassignment queue…</div>;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      {qStatus && <div style={{ color: '#22c55e', fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{qStatus}</div>}
      {queue.length === 0 ? (
        <div style={{ border: '1px dashed #334155', borderRadius: 8, padding: 40, textAlign: 'center', color: '#64748b' }}>
          No rejected assignments awaiting reassignment.
        </div>
      ) : queue.map(item => (
        <div key={item.id} style={{ backgroundColor: '#1e293b', border: '1px solid #7f1d1d44', borderLeft: '4px solid #ef4444', borderRadius: 8, padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <button onClick={() => onNavigate(item.id)} style={{ flex: 1, minWidth: 0, padding: 0, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {item.wo_number && <span>WO {item.wo_number}</span>}
                {item.location_name && <span>{item.location_name}</span>}
                {item.scheduled_date && <span>Scheduled {new Date(item.scheduled_date).toLocaleDateString()}</span>}
              </div>
            </button>
            <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 7px', borderRadius: 5, backgroundColor: '#ef444422', color: '#ef4444', border: '1px solid #ef444444', whiteSpace: 'nowrap' }}>
              Rejected
            </span>
          </div>

          {item.tech_name && (
            <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 12 }}>
              Rejected by: <span style={{ color: '#fca5a5', fontWeight: 700 }}>{item.tech_name}</span>
              {item.tech_career_level && <span style={{ color: '#64748b' }}> · {item.tech_career_level}</span>}
            </div>
          )}
          {item.tech_reject_reason && (
            <div style={{ marginTop: 6, color: '#fca5a5', backgroundColor: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '7px 10px', fontSize: 12 }}>
              "{item.tech_reject_reason}"
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={() => onNavigate(item.id)} style={{ padding: '7px 11px', backgroundColor: '#0f172a', color: '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Open</button>
            <button onClick={() => { setReassigning(item); setNewTechId(''); setQError(''); }} style={{ padding: '7px 13px', backgroundColor: '#1e3a5f', color: '#93c5fd', border: '1px solid #2563eb', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 800 }}>
              Reassign
            </button>
          </div>
        </div>
      ))}

      {reassigning && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.72)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 420, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 20 }}>
            <div style={{ color: '#f8fafc', fontSize: 17, fontWeight: 800, marginBottom: 4 }}>Reassign Tech</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>{reassigning.title}</div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Assign To</div>
            <select value={newTechId} onChange={e => setNewTechId(e.target.value)} style={inputStyle}>
              <option value="">Select technician…</option>
              {techs.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.career_level ? ` · ${u.career_level}` : ''}{u.id === reassigning.assigned_tech ? ' (previous)' : ''}
                </option>
              ))}
            </select>
            {qError && <div style={{ color: '#f87171', fontSize: 13, marginTop: 8, fontWeight: 700 }}>{qError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => setReassigning(null)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitReassign} style={{ flex: 2, padding: 10, backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>Reassign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OpsView() {
  const user = useUser();
  const navigate = useNavigate();
  const [view, setView] = useState('board'); // board | reassign
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active | all | mine | closed
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ title: '', wo_number: '', po_number: '', sn_ticket: '', type: 'project_task', work_type: 'other', priority: 'normal', location_id: '', description: '', assigned_fe: '', assigned_rpm: '', scheduled_date: '' });

  useEffect(() => {
    Promise.all([
      api('/work-items'),
      api('/locations'),
      api('/users'),
    ]).then(([wi, locs, us]) => {
      setItems(Array.isArray(wi) ? wi : []);
      setLocations(Array.isArray(locs) ? locs : []);
      setUsers(Array.isArray(us) ? us : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSync = useCallback(msg => {
    if (msg.type === 'work_item_updated') {
      setItems(prev => prev.map(item =>
        item.id === msg.id ? { ...item, status: msg.status } : item
      ));
    }
  }, []);
  useWorkItemSync(handleSync);

  const filteredItems = items.filter(item => {
    const q = search.toLowerCase();
    const matchSearch = !q || item.title.toLowerCase().includes(q) ||
      (item.sn_ticket || '').toLowerCase().includes(q) ||
      (item.location_name || '').toLowerCase().includes(q);
    const matchFilter =
      filter === 'all'    ? true :
      filter === 'closed' ? item.status === 'closed' :
      filter === 'mine'   ? (item.assigned_tech === user?.userId || item.assigned_fe === user?.userId) :
      !['closed'].includes(item.status);
    return matchSearch && matchFilter;
  }).sort((a, b) => (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]) || new Date(b.created_at) - new Date(a.created_at));

  // Group by status for board view
  const grouped = {};
  const statusOrder = [];
  const addStatus = s => {
    if (!statusOrder.includes(s)) statusOrder.push(s);
    if (!grouped[s]) grouped[s] = [];
  };
  filteredItems.forEach(item => workflowFor(item.work_category).forEach(addStatus));
  filteredItems.forEach(item => {
    if (!grouped[item.status]) addStatus(item.status);
    grouped[item.status].push(item);
  });

  const activeStatuses = statusOrder.filter(s =>
    filter === 'closed' ? s === 'closed' :
    filter === 'all' ? true :
    s !== 'closed'
  ).filter(s => grouped[s]?.length > 0 || filter === 'all');

  const createItem = async () => {
    if (!form.title) return;
    const result = await api('/work-items', {
      method: 'POST',
      body: JSON.stringify({ ...form, location_id: form.location_id || null, assigned_fe: form.assigned_fe || null, assigned_rpm: form.assigned_rpm || null }),
    });
    setItems(prev => [result, ...prev]);
    setShowCreate(false);
    setForm({ title: '', wo_number: '', po_number: '', sn_ticket: '', type: 'project_task', work_type: 'other', priority: 'normal', location_id: '', description: '', assigned_fe: '', assigned_rpm: '', scheduled_date: '' });
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f172a', color: '#64748b', fontFamily: 'sans-serif' }}>
      Loading work items...
    </div>
  );

  const isManager = MANAGER_ROLES.has(user?.role);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#0f172a', fontFamily: 'sans-serif', minHeight: 0 }}>

      {/* Toolbar */}
      <div style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
        {isManager && (
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #334155', marginRight: 4 }}>
            {[{ key: 'board', label: 'Board' }, { key: 'reassign', label: 'Reassign Queue' }].map(v => (
              <button key={v.key} onClick={() => setView(v.key)} style={{
                padding: '5px 12px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: view === v.key ? 800 : 600,
                backgroundColor: view === v.key ? '#2563eb' : 'transparent',
                color: view === v.key ? 'white' : '#94a3b8',
              }}>{v.label}</button>
            ))}
          </div>
        )}
        {view === 'board' && <>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search work items, SN ticket, location..."
            style={{ flex: 1, minWidth: 180, padding: '7px 12px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}
          />
          {['active','mine','all','closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: filter === f ? 'bold' : 'normal',
              backgroundColor: filter === f ? '#2563eb' : '#0f172a', color: filter === f ? 'white' : '#94a3b8',
            }}>{f === 'active' ? 'Active' : f === 'mine' ? 'My Items' : f === 'all' ? 'All' : 'Closed'}</button>
          ))}
          <div style={{ color: '#475569', fontSize: 12 }}>{filteredItems.length} items</div>
          <button onClick={() => setShowCreate(true)} style={{ padding: '7px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold', marginLeft: 'auto' }}>
            + New Work Item
          </button>
        </>}
      </div>

      {/* Reassignment Queue */}
      {view === 'reassign' && (
        <ReassignmentQueue users={users} onNavigate={id => navigate(`/work/${id}`)} />
      )}

      {/* Board */}
      {view === 'board' && <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', display: 'flex', gap: 12, padding: 16, minHeight: 0 }}>
        {activeStatuses.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#334155', fontSize: 14 }}>
            No work items match the current filter.
          </div>
        ) : activeStatuses.map(status => (
          <div key={status} style={{ flexShrink: 0, width: 280, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[status] || '#64748b' }} />
                <span style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 12 }}>{STATUS_LABELS[status] || status}</span>
              </div>
              <span style={{ backgroundColor: '#1e293b', color: '#64748b', fontSize: 11, fontWeight: 'bold', padding: '2px 7px', borderRadius: 10 }}>
                {grouped[status]?.length || 0}
              </span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              {grouped[status]?.map(item => (
                <WorkCard key={item.id} item={item} onClick={id => navigate(`/work/${id}`)} />
              ))}
            </div>
          </div>
        ))}
      </div>}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 20 }}>
          <div style={{ backgroundColor: '#1e293b', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, border: '1px solid #334155', marginTop: 40 }}>
            <div style={{ fontWeight: 'bold', fontSize: 17, color: '#e2e8f0', marginBottom: 18 }}>New Work Item</div>

            {[
              { label: 'Title *', key: 'title', type: 'text', ph: 'Brief description of work' },
              { label: 'Work Order #', key: 'wo_number', type: 'text', ph: 'e.g. WO-2026-001' },
              { label: 'PO #', key: 'po_number', type: 'text', ph: 'e.g. PO-45678' },
              { label: 'SN Ticket #', key: 'sn_ticket', type: 'text', ph: 'e.g. INC0012345' },
              { label: 'Scheduled Date', key: 'scheduled_date', type: 'date', ph: '' },
            ].map(({ label, key, type, ph }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>
                <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} placeholder={ph}
                  style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            ))}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              {[
                { label: 'Type', key: 'type', opts: [['project_task','Project Task'],['incident','Incident'],['maintenance','Maintenance']] },
                { label: 'Work Type', key: 'work_type', opts: [['install_equipment','Install Equipment'],['add_jumpers','Add Jumpers'],['splice_fiber','Splice Fiber'],['migrate','Migrate'],['decommission','Decommission'],['configure','Configure'],['other','Other']] },
                { label: 'Priority', key: 'priority', opts: [['normal','Normal'],['low','Low'],['high','High'],['critical','Critical']] },
                { label: 'Location', key: 'location_id', opts: [['','— None —'], ...locations.map(l => [l.id, l.name])] },
              ].map(({ label, key, opts }) => (
                <div key={key}>
                  <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>
                  <select value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}>
                    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              {[
                { label: 'Assign FE', key: 'assigned_fe', role: 'field_engineer' },
                { label: 'Assign RPM', key: 'assigned_rpm', role: 'rpm' },
              ].map(({ label, key, role }) => (
                <div key={key}>
                  <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>
                  <select value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}>
                    <option value=''>— Unassigned —</option>
                    {users.filter(u => u.role === role || u.role === 'admin').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Description</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Scope of work, notes, special requirements..."
                style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowCreate(false)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={createItem} disabled={!form.title} style={{ flex: 2, padding: 10, backgroundColor: form.title ? '#2563eb' : '#1e3a5f', color: form.title ? 'white' : '#475569', border: 'none', borderRadius: 7, cursor: form.title ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 'bold' }}>
                Create Work Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
